import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Description, Divider, List, semanticColor, Svg, Table, TableCell, useDialog } from '@repo/ui';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { ResultTableRow } from '@/components/tax-refund/apply/result/ResultTableRow';
import { checkRefund } from '@/components/tax-refund/common/graphql/query/checkRefund';
import ApplyResultFaqView from '@/components/tax-refund/common/view/ApplyResultFaqView';
import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { getCorporationDefaultInfo, getIndividualDefaultInfo } from '@/lib/constants/info-contents';
import { COMMON_PATHS, CORPORATION_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import { cancelDataAtom } from '@/lib/stores/cancel-data';
import { ApplyResultData } from '@/lib/types/request';
import { formatAccountBankNo } from '@/lib/utils/stringFormat';

export const ApplyResultDeclareCompleteContent = ({ data }: { data: ApplyResultData }) => {
  const isCorps = data.businessType === 'corps';
  const displayName = isCorps ? (data.fullName ?? data.name) : data.name;
  const router = useRouter();
  const relayEnvironment = useRelayEnvironment();
  const { sendClickEvent, sendViewEvent } = useUserEventLogger();
  const { showDialog } = useDialog();
  const setCancelData = useSetAtom(cancelDataAtom);
  const [disabledCancelButton, setDisabledCancelButton] = useState(false);
  const { hasRegisteredCard, cardName, cardLogo, cardNumber } = data.paymentCard;

  const faqContents = [
    {
      title: '환급금을 더 빨리 받고 싶어요.',
      content:
        '비즈넵 환급에서 경정청구 신고를 완료한 후 국세청 검토까지 완료되어야 최종 환급이 결정됩니다. 국세청 검토는 신고일로부터 평균적으로 2개월 가량 소요됩니다.\n편하게 확인하실 수 있도록 국세청 환급 결과 확인이 가능할 때 카카오톡 알림톡으로 안내해드리겠습니다.'
    },
    {
      title: '환급 신청한 지 2개월이 지났어요.',
      content: '국세청에서 추가 확인이 필요한 경우 2개월 이상 소요될 수 있습니다. 세부 진행사항이 궁금하시다면 고객센터로 문의해주세요.'
    },
    {
      title: '세금을 추가 납부하게 될 수도 있나요?',
      content:
        '비즈넵 환급에서는 제휴 세무사의 검토를 진행하고 있어 고객님께서 추가 납부하시는 일이 없도록 최대한 보수적으로 신고하고 있으니 안심하셔도 됩니다.'
    },
    {
      title: '환급받을 계좌를 변경하고 싶어요.',
      content: '신고가 완료되면 국세청에서 검토가 진행되기 때문에 계좌 변경이 어렵습니다. 국세 환급은 본인 명의의 계좌로만 지급이 가능한 점 참고해주세요.'
    },
    {
      title: '환급금을 어떻게 찾았나요?',
      content: isCorps
        ? '비즈넵 환급은 과거 5년 동안 사업소득에 대한 종합소득세를 신고하며 놓쳤던 세액 공제, 세액 감면 항목들을 고도화된 AI 기술로 찾아냅니다. 그리고 비즈넵 환급과 제휴한 세무 전문가가 탐색 결과값을 검토하여 놓치는 부분 없이 환급금을 찾아드리고 있습니다.'
        : '비즈넵 환급은 과거 5년간 개인사업자의 종합소득세와 개인의 자산 양도 시 발생한 양도소득세, 법인사업자에 대한 법인세를 신고하며 놓쳤던 세액 공제, 세액 감면 항목들을 고도화된 AI 기술로 찾아냅니다. 그리고 비즈넵 환급과 제휴한 세무 전문가가 탐색 결과값을 검토하여 놓치는 부분 없이 환급금을 찾아드리고 있습니다.'
    },
    {
      title: '농어촌 특별세 납부고지서가 왔는데 납부해야 하나요?',
      content:
        '농어촌 특별세는 특정 조세 혜택을 받은 환급 건에 적용되는 세금으로 혜택 금액의 20%를 납부해야 합니다. 농어촌 특별세 납부고지서를 받으셨다면 특정 조세 혜택이 잘 적용되어 환급 신청이 완료된 경우입니다. 비즈넵 환급에서는 농어촌 특별세를 반영해 신고하고 있으며 농어촌 특별세로 고지된 금액보다 더 큰 금액으로 환급이 이루어지니 고지서를 확인해 납부해주시면 됩니다.'
    }
  ];
  const infoContents = isCorps ? getCorporationDefaultInfo() : getIndividualDefaultInfo(hasRegisteredCard);

  const cancelClickEvent = `${isCorps ? 'corps' : 'indis'}-result_cancel_declaration`;
  const refreshDialogEvent = `${isCorps ? 'corps' : 'indis'}-result_cancel_refresh-dialog`;
  const refreshDialogOkEvent = `${refreshDialogEvent}-ok`;

  const handleCardClick = () => {
    if (isCorps) {
      return;
    }

    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const handleClickCancel = async () => {
    sendClickEvent(cancelClickEvent);
    setDisabledCancelButton(true);

    const refund = await checkRefund(relayEnvironment, data.refundId);
    const isCancelable = refund?.cancelStage !== 'Canceling' && refund?.refundStatus === 'ApplyComplete' && refund?.refundStage === 'Declare';

    if (isCancelable) {
      setCancelData({
        refundId: data.refundId,
        name: displayName,
        businessType: data.businessType
      });
      router.push({ pathname: isCorps ? CORPORATION_PATHS.법인취소방어 : INDIVIDUAL_PATHS.개인취소방어, query: router.query });
      setDisabledCancelButton(false);
      return;
    }

    sendViewEvent(refreshDialogEvent, undefined, { isAllowRetry: true });
    showDialog({
      title: '진행 단계에 변동이 있어 새로고침이 필요해요',
      description: '확인을 누르시면 현재 상태로 페이지가 새로고침돼요.',
      buttons: {
        primary: {
          onClick: () => {
            sendClickEvent(refreshDialogOkEvent);
            setDisabledCancelButton(false);
            router.reload();
          }
        }
      }
    });
  };

  useEffect(() => {
    setCancelData(undefined);
  }, [setCancelData]);

  return (
    <div>
      <div className={'px-4.5 py-normal-small'}>
        <Description
          title={isCorps ? `’${displayName}’의 신고서를 국세청에서 최종 검토 중이에요` : `${displayName}님의 신고서를\n국세청에서 최종 검토 중이에요`}
          size={'medium'}
          graphic={<Svg src="icg_document-credential-national_md.svg" width={80} height={80} />}
        />
      </div>
      <Divider size={'large'} />
      <div className={'px-4.5 py-tiny-large'}>
        <div className={'pb-normal-large'}>
          <div className={'body-large-regular text-neutral-foreground-low pt-normal-large'}>{isCorps ? '법인세 예상 환급액' : '종합소득세 예상 환급액'}</div>
          <div className={'text-neutral-foreground-main heading-medium-bold'}>{data.refundAmount}</div>
          <Divider className={'my-normal-medium'} />
          <ResultTableRow label={'환급 신청일'} value={data.applyDate} />
          <ResultTableRow label={'예상 환급일'} value={'신청일부터 2~3개월'} />
          {data.applicationInfo && (
            <ResultTableRow label={'환급 계좌'} value={formatAccountBankNo(data.applicationInfo?.bank, data.applicationInfo?.accountNo)} />
          )}
          <Table
            headerCell={<TableCell variant={'header'}>이용료</TableCell>}
            contentCell={
              <TableCell className={'flex-1 text-right'}>
                <div>
                  <p className={'mb-1'}>결정 환급액의 {data.defaultFeeRate}%</p>
                  <p className={'text-13 text-gray-500'}>(부가세 별도)</p>
                </div>
              </TableCell>
            }
          />
          {data.discountRate !== '0' && (
            <Table
              headerCell={<TableCell variant={'header'}>이용료 할인</TableCell>}
              contentCell={
                <TableCell className={'flex-1 text-right'}>
                  <div>
                    <p className={'mb-1'}>제휴 할인 {data.discountRate}% 적용</p>
                    <p className={'text-13 text-gray-500'}>
                      결정 환급액의 <span className={'line-through'}>{data.defaultFeeRate}%</span>-&gt;{data.feeRate}% (vat별도)
                    </p>
                  </div>
                </TableCell>
              }
            />
          )}
          {!isCorps && hasRegisteredCard && cardNumber && (
            <Table
              headerCell={
                <TableCell className={'flex items-center'} variant={'header'}>
                  결제 카드
                </TableCell>
              }
              contentCell={
                <>
                  <TableCell className={'flex items-center flex-1 justify-end'}>
                    <div className={'flex gap-tiny-medium text-[15px]'}>
                      <Svg src={cardLogo} width={24} height={24} />
                      {cardName}({cardNumber.slice(-4)})
                    </div>
                  </TableCell>
                  <TableCell>
                    <BoxButton size={'small'} variant={'tonal-secondary'} onClick={handleCardClick}>
                      변경
                    </BoxButton>
                  </TableCell>
                </>
              }
            />
          )}
        </div>
        <ApplyResultInfoView data={infoContents} />
        <Divider />
        <div className={'pt-tiny-large'}>
          <List
            title={''}
            primaryAction
            onClick={handleClickCancel}
            state={disabledCancelButton ? 'disabled' : 'enabled'}
            supportingElement={
              <div className={`flex items-center text-primary-foreground-main body-small-medium ${disabledCancelButton && 'opacity-40'}`}>
                신청 취소
                <Svg src={'ic_chevron-right.svg'} width={18} height={18} color={semanticColor.primary.foreground.main} />
              </div>
            }
          />
        </div>
      </div>
      <Divider size={'large'} />
      <ApplyResultFaqView data={faqContents} clickEventName={`${isCorps ? 'corps' : 'indis'}-result_faq_declaration`} />
      <div className={'pb-normal-medium'} />
    </div>
  );
};
